import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Modal,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Chip,
  Checkbox,
} from "@mui/material";
import { useEffect, useState } from "react";
import { DrawerForm } from "../../Common/styles/drawer";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../../Common/styles/modal";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import http from "../../../utils/http";
import { AxiosResponse } from "axios";

type Props = {
  showModal: boolean;
  closeModal: () => void;
  user1Id: string;
  user2Id: string;
  duplicateSetId: string;
  onSuccess: () => void;
};

interface Participant {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dob: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  gender?: string;
  lang?: string;
  occupation?: string;
  address?: {
    addressLine1?: string;
    addressLine2?: string;
    zipcode?: string;
    city?: string;
    state?: string;
    country?: string;
  };
}

interface ComparisonData {
  id: string;
  user1: string;
  user2: string;
  confidenceScore: string;
  status: string;
  matchDetails: {
    level: string;
    reason: string;
    matchType: string;
    lastNameMetaphone?: string;
    firstNameMetaphone?: string;
    lastNameMatched?: boolean;
    firstNameMatched?: boolean;
    matchedLastNameMetaphone?: string;
    matchedFirstNameMetaphone?: string;
    zipCode?: string;
    dobMatched?: boolean;
    genderMatched?: boolean;
    zipCodeMatched?: boolean;
    languageMatched?: boolean;
    occupationMatched?: boolean;
  };
  participants: Participant[];
  goldenRecordId: string | null;
  resolvedAt: string | null;
  createdAt: string;
  dismissReason?: string | null;
}

const formatStatus = (status: string) => {
  return status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

const getStatusColor = (
  status: string
): "warning" | "success" | "error" | "default" => {
  switch (status) {
    case "PENDING_REVIEW":
      return "warning";
    case "MERGED":
      return "success";
    case "NOT_DUPLICATE":
    case "DISMISSED":
      return "default";
    default:
      return "default";
  }
};

const getConfidenceScoreColor = (score: number): string => {
  if (score >= 90) {
    return "#F87171"; // Red
  } else if (score >= 80) {
    return "#FBBF24"; // Yellow/Orange
  } else {
    return "#60A5FA"; // Blue
  }
};

type DismissDialogProps = {
  show: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
};

const DismissDialog = ({ show, onClose, onConfirm }: DismissDialogProps) => {
  const [reason, setReason] = useState("");

  const handleConfirm = () => {
    if (reason.trim()) {
      onConfirm(reason);
      setReason("");
      onClose();
    }
  };

  return (
    <Dialog open={show} onClose={onClose}>
      <DialogTitle>Dismiss Duplicate</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Please provide a reason for dismissing this duplicate set.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          multiline
          rows={3}
          placeholder="Enter reason..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!reason.trim()}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const customModalStyles = {
  ...ModalDrawerStyles,
  left: "30%",
  width: {
    xs: "96vw",
    md: "70vw",
  },
};
const UserComparisonModal = ({
  showModal,
  closeModal,
  user1Id,
  user2Id,
  duplicateSetId,
  onSuccess,
}: Props) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ComparisonData | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [goldenRecordId, setGoldenRecordId] = useState<string | null>(null);
  const [showDismissDialog, setShowDismissDialog] = useState(false);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setLoading(true);
        // Reset golden record state before fetching new data
        setGoldenRecordId(null);

        const response = await http.get(
          `/admin/registry/participant-duplicates/${duplicateSetId}/details`
        );
        setData(response.data.data);

        // Only set golden record if it exists in the API response
        if (response.data.data.goldenRecordId) {
          setGoldenRecordId(response.data.data.goldenRecordId);
        }
      } catch (err) {
        errorToastMessage(err as Error);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchUserDetails();
  }, [duplicateSetId]);

  const handleToggleField = (fieldName: string) => {
    setSelectedFields((prev) =>
      prev.includes(fieldName)
        ? prev.filter((f) => f !== fieldName)
        : [...prev, fieldName]
    );
  };

  const handleMarkAsGolden = (participantId: string) => {
    setGoldenRecordId(participantId);
    setSelectedFields([]);
  };

  const handleDismiss = async (reason: string) => {
    try {
      setSubmitting(true);
      await http.post(
        `/admin/registry/participant-duplicates/${duplicateSetId}/dismiss`,
        {
          reason,
        }
      );

      toastMessage("success", "Duplicate set dismissed successfully");
      closeModal();
      onSuccess();
    } catch (err) {
      errorToastMessage(err as Error);
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMergeRecords = async () => {
    if (!goldenRecordId || !data) return;

    try {
      setSubmitting(true);
      const duplicateRecordId = data.participants.find(
        (p) => p.id !== goldenRecordId
      )?.id;

      if (!duplicateRecordId) {
        throw new Error("Could not find duplicate record ID");
      }

      if (selectedFields?.length > 0) {
        await http.post("/admin/registry/participant-duplicates/copy-fields", {
          goldenUserId: goldenRecordId,
          userId: duplicateRecordId,
          fields: selectedFields,
        });
      }

      const res: AxiosResponse = await http.post(
        `/admin/registry/participant-duplicates/merge`,
        {
          goldenRecordId,
          duplicateRecordId,
        }
      );

      toastMessage(
        "success",
        res?.data?.message ? res.data.message : "Records merged successfully"
      );
      closeModal();
      onSuccess();
    } catch (err) {
      errorToastMessage(err as Error);
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const getHighlightedFields = (): string[] => {
    if (!data?.matchDetails) return [];

    const { matchDetails } = data;
    const fields: string[] = [];

    const fieldMap: Record<string, string> = {
      firstNameMatched: "firstName",
      lastNameMatched: "lastName",
      dobMatched: "dob",
      genderMatched: "gender",
      zipCodeMatched: "zipcode",
      languageMatched: "lang",
      occupationMatched: "occupation",
    };

    for (const key in fieldMap) {
      if ((matchDetails as any)[key]) {
        fields.push(fieldMap[key]);
      }
    }

    if (matchDetails.matchType === "EMAIL") {
      fields.push("email");
    }
    if (matchDetails.matchType === "PHONE") {
      fields.push("phoneNumber");
    }

    return fields;
  };

  const renderComparisonTable = () => {
    if (!data) return null;

    const [user1, user2] = data.participants;
    const isPendingReview = data.status === "PENDING_REVIEW";
    const highlightedFields = getHighlightedFields();

    const fields = [
      { key: "firstName", label: "First Name" },
      { key: "lastName", label: "Last Name" },
      { key: "email", label: "Email" },
      { key: "phoneNumber", label: "Phone" },
      { key: "dob", label: "Date of Birth" },
      { key: "gender", label: "Gender" },
      { key: "lang", label: "Language" },
      { key: "occupation", label: "Occupation" },
      { key: "addressLine1", label: "Address Line 1", isAddress: true },
      { key: "addressLine2", label: "Address Line 2", isAddress: true },
      { key: "city", label: "City", isAddress: true },
      { key: "state", label: "State", isAddress: true },
      { key: "zipcode", label: "Zip Code", isAddress: true },
      { key: "country", label: "Country", isAddress: true },
    ];

    const getValue = (participant: Participant, field: any) => {
      if (field.isAddress) {
        return (
          participant.address?.[field.key as keyof Participant["address"]] ||
          "--"
        );
      }
      const value = participant[field.key as keyof Participant];
      return String(value || "--");
    };

    return (
      <Box>
        {/* Table Header */}
        <Box
          sx={{
            display: "flex",
            mb: 1,
            borderBottom: 1,
            borderColor: "divider",
            pb: 1,
          }}
        >
          <Typography sx={{ flex: 1, fontWeight: "bold" }}>Field</Typography>
          <Typography sx={{ flex: 2, fontWeight: "bold" }}>
            User 1 {goldenRecordId === user1.id && "(Golden Record)"}
          </Typography>
          <Typography sx={{ flex: 2, fontWeight: "bold" }}>
            User 2 {goldenRecordId === user2.id && "(Golden Record)"}
          </Typography>
        </Box>

        {/* Table Body */}
        {fields.map((field) => {
          const user1Value = getValue(user1, field);
          const user2Value = getValue(user2, field);
          const isHighlighted = highlightedFields.includes(field.key);

          const showCheckboxForUser1 =
            isPendingReview &&
            goldenRecordId &&
            goldenRecordId !== user1.id &&
            !["email", "lang"].includes(field.key);
          const showCheckboxForUser2 =
            isPendingReview &&
            goldenRecordId &&
            goldenRecordId !== user2.id &&
            !["email", "lang"].includes(field.key);

          return (
            <Box
              key={field.key}
              sx={{
                display: "flex",
                py: 1.5,
                borderBottom: 1,
                backgroundColor: isHighlighted
                  ? "secondary.main"
                  : "transparent",
                borderLeft: isHighlighted ? "4px solid" : "none",
                borderColor: isHighlighted ? "primary.main" : "grey.200",
                pl: isHighlighted ? 1.5 : 0,
              }}
            >
              <Typography sx={{ flex: 1, color: "text.secondary" }}>
                {field.label}
              </Typography>
              <Box sx={{ flex: 2, display: "flex", alignItems: "center" }}>
                {showCheckboxForUser1 && (
                  <Checkbox
                    size="small"
                    checked={selectedFields.includes(field.key)}
                    onChange={() => handleToggleField(field.key)}
                    sx={{ p: 0, mr: 1 }}
                  />
                )}
                <Typography>{user1Value}</Typography>
              </Box>
              <Box sx={{ flex: 2, display: "flex", alignItems: "center" }}>
                {showCheckboxForUser2 && (
                  <Checkbox
                    size="small"
                    checked={selectedFields.includes(field.key)}
                    onChange={() => handleToggleField(field.key)}
                    sx={{ p: 0, mr: 1 }}
                  />
                )}
                <Typography>{user2Value}</Typography>
              </Box>
            </Box>
          );
        })}

        {/* Action Buttons */}
        {isPendingReview && (
          <Box sx={{ display: "flex", mt: 2 }}>
            <Box sx={{ flex: 1 }} /> {/* Empty column for spacing */}
            <Box sx={{ flex: 2, px: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleMarkAsGolden(user1.id)}
                disabled={goldenRecordId === user1.id}
              >
                {goldenRecordId === user1.id
                  ? "Golden Record"
                  : "Mark User 1 as Golden"}
              </Button>
            </Box>
            <Box sx={{ flex: 2, px: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleMarkAsGolden(user2.id)}
                disabled={goldenRecordId === user2.id}
              >
                {goldenRecordId === user2.id
                  ? "Golden Record"
                  : "Mark User 2 as Golden"}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={customModalStyles}>
        <ModalHeader
          title="User Comparison Details"
          onCloseClick={closeModal}
        />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box style={DrawerForm}>
            <Box>
              {data && (
                <Box
                  sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}
                >
                  <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                    Status:
                  </Typography>
                  <Chip
                    label={formatStatus(data.status)}
                    color={getStatusColor(data.status)}
                    size="small"
                  />
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "medium", ml: 2 }}
                  >
                    Confidence Score:
                  </Typography>
                  <Chip
                    label={`${parseFloat(data.confidenceScore).toFixed(
                      2
                    )}% match`}
                    size="small"
                    sx={{
                      backgroundColor: getConfidenceScoreColor(
                        parseFloat(data.confidenceScore)
                      ),
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              )}

              <Box sx={{ mb: 3 }}>{renderComparisonTable()}</Box>

              {data?.dismissReason && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    backgroundColor: "grey.100",
                    borderRadius: 1,
                    borderLeft: "4px solid",
                    borderColor: "grey.400",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "bold", mb: 1, color: "text.secondary" }}
                  >
                   Reason:
                  </Typography>
                  <Typography variant="body1" sx={{ color: "text.primary" }}>
                    {data.dismissReason}
                  </Typography>
                </Box>
              )}
            </Box>

            {data?.status === "PENDING_REVIEW" && (
              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!submitting ? (
                    <>
                      <Button
                        variant="outlined"
                        onClick={() => setShowDismissDialog(true)}
                        disabled={data?.status !== "PENDING_REVIEW"}
                      >
                        Not a Duplicate
                      </Button>
                      <Button
                        variant="contained"
                        sx={{ backgroundColor: "#7C3AED" }}
                        onClick={handleMergeRecords}
                        disabled={
                          !goldenRecordId || data?.status !== "PENDING_REVIEW"
                        }
                      >
                        {selectedFields?.length > 0
                          ? "Copy & Merge Records"
                          : "Merge Records"}
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
        <DismissDialog
          show={showDismissDialog}
          onClose={() => setShowDismissDialog(false)}
          onConfirm={handleDismiss}
        />
      </Box>
    </Modal>
  );
};

export default UserComparisonModal;
